import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SendModal = ({ via, onSend, onClose, isProcessing, initialPhone }) => {
  const [contact, setContact] = useState(initialPhone || '');
  const touchedRef = useRef(false);
  const label = via === 'whatsapp' ? 'Número de WhatsApp' : 'Correo electrónico';
  const placeholder = via === 'whatsapp' ? '+52 55 1234 5678' : 'cliente@ejemplo.com';

  useEffect(() => {
    if (!touchedRef?.current) {
      setContact(initialPhone || '');
    }
  }, [initialPhone]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <Icon name={via === 'whatsapp' ? 'MessageCircle' : 'Mail'} size={24} className="text-primary" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Enviar por {via === 'whatsapp' ? 'WhatsApp' : 'Email'}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Ingrese el {label?.toLowerCase()} del cliente para enviar la cotización.
        </p>
        <input
          type={via === 'whatsapp' ? 'tel' : 'email'}
          placeholder={placeholder}
          value={contact}
          onChange={e => {
            touchedRef.current = true;
            setContact(e?.target?.value);
          }}
          className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary mb-4"
        />
        <div className="flex gap-3">
          <Button
            variant="default"
            iconName={via === 'whatsapp' ? 'MessageCircle' : 'Mail'}
            iconPosition="left"
            onClick={() => contact?.trim() && onSend(contact?.trim())}
            disabled={!contact?.trim() || isProcessing}
            loading={isProcessing}
            fullWidth
          >
            Enviar
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isProcessing} fullWidth>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

const QuoteActions = ({ 
  hasItems = false,
  onSendToSuppliers,
  onSendToClient,
  onMarkAsAuthorized,
  onSaveAsDraft,
  onSendWhatsApp,
  onSendEmail,
  onPreviewPdf,
  onGeneratePdf,
  onPrintPdf,
  isProcessing = false,
  quotationStatus = null,
  clientPhone = '',
  whatsAppError = '',
}) => {
  const [sendModal, setSendModal] = useState(null); // null | 'whatsapp' | 'email'

  const handleSend = async (contact) => {
    if (sendModal === 'whatsapp' && onSendWhatsApp) {
      await onSendWhatsApp(contact);
    } else if (sendModal === 'email' && onSendEmail) {
      await onSendEmail(contact);
    }
    setSendModal(null);
  };

  const isSent = quotationStatus === 'enviada';
  const isApproved = quotationStatus === 'aprobada';
  const isRejected = quotationStatus === 'rechazada';

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Zap" size={24} className="text-accent" />
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Acciones de Cotización
          </h2>
          {quotationStatus && (
            <span className={`ml-auto text-xs px-2 py-1 rounded-full font-medium ${
              isApproved ? 'bg-success/10 text-success' : isRejected ?'bg-destructive/10 text-destructive': isSent ?'bg-primary/10 text-primary': 'bg-muted text-muted-foreground'
            }`}>
              {isApproved ? 'Aprobada' : isRejected ? 'Rechazada' : isSent ? 'Enviada' : 'Borrador'}
            </span>
          )}
        </div>

        {/* PDF Section */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={18} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Documento PDF</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Button
              variant="outline"
              iconName="Eye"
              iconPosition="left"
              onClick={onPreviewPdf}
              disabled={!hasItems || isProcessing}
              fullWidth
            >
              Vista previa PDF
            </Button>
            <Button
              variant="default"
              iconName="Download"
              iconPosition="left"
              onClick={onGeneratePdf}
              disabled={!hasItems || isProcessing}
              fullWidth
            >
              Generar PDF
            </Button>
            <Button
              variant="secondary"
              iconName="Printer"
              iconPosition="left"
              onClick={onPrintPdf}
              disabled={!hasItems || isProcessing}
              fullWidth
            >
              Imprimir PDF
            </Button>
          </div>
        </div>

        {/* Send to Client section */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="Send" size={18} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Enviar al Cliente</span>
            {isSent && <Icon name="CheckCircle" size={16} className="text-success ml-auto" />}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="default"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => setSendModal('whatsapp')}
              disabled={!hasItems || isProcessing || isApproved || isRejected}
              fullWidth
            >
              Enviar por WhatsApp
            </Button>
            <Button
              variant="secondary"
              iconName="Mail"
              iconPosition="left"
              onClick={() => setSendModal('email')}
              disabled={!hasItems || isProcessing || isApproved || isRejected}
              fullWidth
            >
              Enviar por Email
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            variant="secondary"
            iconName="Mail"
            iconPosition="left"
            onClick={onSendToClient}
            disabled={!hasItems || isProcessing}
            fullWidth
          >
            Enviar al Cliente (PDF)
          </Button>

          <Button
            variant="success"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={onMarkAsAuthorized}
            disabled={!hasItems || isProcessing}
            fullWidth
          >
            Marcar como Autorizada
          </Button>

          <Button
            variant="outline"
            iconName="Save"
            iconPosition="left"
            onClick={onSaveAsDraft}
            disabled={!hasItems || isProcessing}
            fullWidth
          >
            Guardar
          </Button>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-heading font-semibold text-foreground mb-2">
                Información de Acciones
              </h3>
              <ul className="space-y-1 text-xs caption text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>WhatsApp / Email: Envía cotización al cliente y registra el envío</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Marcar como autorizada: Cambia el estado de la OT a "Aprobado"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Guardar: Guarda la cotización en "Cotizaciones guardadas"</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {whatsAppError ? (
        <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2 mt-1">
          <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-xs text-destructive font-medium">{whatsAppError}</p>
        </div>
      ) : null}

      {sendModal && (
        <SendModal
          via={sendModal}
          onSend={handleSend}
          onClose={() => setSendModal(null)}
          isProcessing={isProcessing}
          initialPhone={sendModal === 'whatsapp' ? clientPhone : ''}
        />
      )}
    </>
  );
};

export default QuoteActions;